/**
 * GenerationEquipmentImportDialog — the EQUIPMENT LIBRARY (Duty-First Finder).
 *
 * One tool over every equipment evidence source (Equipment BOM, OEM register,
 * company catalog, manual entry). The scenario's duty and documented site
 * limits are pinned on top, and every candidate is ranked by the EXACT solver
 * screen where one defensibly exists:
 *  - real envelopes rank on the workbench's own screening rows;
 *  - full prefills rank on a transient user-entry screen ONLY when their
 *    heat-rate basis is explicitly published — derived/unstated bases sit in
 *    a labeled "confirm basis" band, and partial data is never ranked.
 * Actions route through the same resolution ladder as everywhere else; this
 * dialog never writes solver inputs directly.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, DatabaseZap, X } from 'lucide-react';

import type { OemGenerationSourceRegister } from '../../../types/oemGenerationCatalog';
import type { EquipmentCatalogEntry, SupplyChainPlan } from '../../../types/supplyChain';
import type {
  GenerationImpactScenarioInput,
  GenerationUserEquipmentEntry,
} from '../../../types/generationImpact';
import {
  listGenerationBomImportCandidates,
  type GenerationUserEquipmentPrefill,
} from '../../../lib/generationImpact/equipmentImportSources';
import {
  buildEquipmentFinderCandidates,
  rankEquipmentFinderCandidates,
  type EquipmentFinderCandidate,
  type FinderBand,
  type FinderRankMode,
  type FinderSource,
} from '../../../lib/generationImpact/equipmentFinderRanking';
import type { GenerationEquipmentScreenRow } from '../../../lib/generationImpact/generationEquipmentCatalog';
import type { SiteGridFrequencyHz } from '../../../lib/generationImpact/gridFrequency';
import { OEM_GENERATION_SOURCE_REGISTER } from '../../../lib/generationImpact/generationOemSourceRegister';
import { resolveEquipmentCatalog } from '../../../lib/resolveEquipmentCatalog';
import { EquipmentLibraryBrowser } from '../../equipmentLibrary/EquipmentLibraryBrowser';

const METRES_PER_FOOT = 0.3048;

export interface GenerationEquipmentImportDialogProps {
  open: boolean;
  /** Duty context + constraint limits + ranking basis. */
  input: GenerationImpactScenarioInput;
  criterionLabel: string;
  parcelAcres?: number | null;
  /** The admitted screening rows the workbench already solved. */
  equipmentRows: readonly GenerationEquipmentScreenRow[];
  initialSource?: FinderSource | 'all';
  plan?: SupplyChainPlan;
  /** Owning tenant used to resolve private products and commercial patches. */
  companyId?: string | null;
  register?: OemGenerationSourceRegister;
  shortlistedCatalogIds: readonly string[];
  appliedCatalogId?: string;
  userEquipment: readonly GenerationUserEquipmentEntry[];
  onClose: () => void;
  onShortlistCatalog: (catalogId: string) => void;
  onPrefill: (prefill: GenerationUserEquipmentPrefill) => void;
  onOpenBlank: () => void;
  /** Optional deep link to the Equipment (BOM) tab for the empty BOM state. */
  onOpenBom?: () => void;
  /** Optional override for the catalog fetch (tests). */
  fetchCatalogEntries?: () => Promise<EquipmentCatalogEntry[]>;
  /** Site grid frequency; null/omitted = unknown, mismatch flags suppressed. */
  siteGridFrequencyHz?: SiteGridFrequencyHz | null;
}

const RANK_MODES: ReadonlyArray<[FinderRankMode, string]> = [
  ['best-fit', 'Rank · best fit'],
  ['mw', 'MW'],
  ['footprint', 'Footprint'],
  ['heat-rate', 'Heat rate'],
  ['lead-time', 'Lead time'],
];

const BAND_HEADINGS: Record<FinderBand, string> = {
  ranked: 'Ranked by the exact solver screen',
  'confirm-basis': 'Confirm the heating-value basis to rank',
  'needs-data': 'Needs data to rank',
};

function fmt(value: number, digits = 0): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: digits }).format(value);
}

function tierBadge(candidate: EquipmentFinderCandidate): { label: string; className: string } {
  const resolution = candidate.resolution;
  if (resolution.tier === 'catalog-envelope') {
    return resolution.admission === 'apply-approved'
      ? { label: 'Real envelope', className: 'border-success/40 bg-success/10 text-success' }
      : { label: 'Real envelope · confirm OEM', className: 'border-warning/40 bg-warning/10 text-warning' };
  }
  if (resolution.tier === 'prefill-full') {
    return { label: 'Prefill · full data', className: 'border-primary/40 bg-primary/5 text-primary' };
  }
  if (resolution.tier === 'prefill-partial') {
    return { label: 'Prefill · partial', className: 'border-border text-text-secondary' };
  }
  return { label: 'Needs data', className: 'border-border text-text-muted' };
}

const sourceBadgeClass: Record<FinderSource, string> = {
  bom: 'border-warning/40 bg-warning/10 text-warning',
  oem: 'border-info/40 bg-info/10 text-info',
  catalog: 'border-border bg-background text-text-secondary',
};
const sourceBadgeLabel: Record<FinderSource, string> = {
  bom: 'Your BOM',
  oem: 'OEM',
  catalog: 'Catalog',
};

export function GenerationEquipmentImportDialog({
  open,
  input,
  criterionLabel,
  parcelAcres = null,
  equipmentRows,
  initialSource = 'all',
  plan,
  companyId,
  register = OEM_GENERATION_SOURCE_REGISTER,
  shortlistedCatalogIds,
  appliedCatalogId,
  userEquipment,
  onClose,
  onShortlistCatalog,
  onPrefill,
  onOpenBlank,
  onOpenBom,
  fetchCatalogEntries,
  siteGridFrequencyHz,
}: GenerationEquipmentImportDialogProps): React.JSX.Element | null {
  const [rankMode, setRankMode] = useState<FinderRankMode>('best-fit');
  const [source, setSource] = useState<FinderSource | 'all'>(initialSource);
  const [search, setSearch] = useState('');
  const [catalogEntries, setCatalogEntries] = useState<EquipmentCatalogEntry[] | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [libraryMode, setLibraryMode] = useState<'scenario-fit' | 'full-oem'>('scenario-fit');

  useEffect(() => {
    if (!open || catalogEntries != null || catalogError != null) return;
    let cancelled = false;
    void (async () => {
      try {
        const fetch = fetchCatalogEntries ?? (() => resolveEquipmentCatalog(companyId));
        const entries = await fetch();
        if (!cancelled) setCatalogEntries(entries);
      } catch (error) {
        if (!cancelled) setCatalogError((error as Error).message || 'Equipment catalog could not be loaded.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogEntries, catalogError, companyId, fetchCatalogEntries, open]);

  const bomCount = useMemo(() => listGenerationBomImportCandidates(plan).length, [plan]);

  const candidates = useMemo(() => {
    if (!open) return [];
    return buildEquipmentFinderCandidates({
      input,
      criterionLabel,
      ...(plan ? { plan } : {}),
      register,
      ...(catalogEntries ? { catalogEntries } : {}),
      equipmentRows,
      ...(siteGridFrequencyHz !== undefined ? { siteGridFrequencyHz } : {}),
    });
  }, [catalogEntries, criterionLabel, equipmentRows, input, open, plan, register, siteGridFrequencyHz]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = candidates.filter(candidate => (
      (source === 'all' || candidate.source === source)
      && (query === ''
        || `${candidate.title} ${candidate.subtitle ?? ''}`.toLowerCase().includes(query))
    ));
    return rankEquipmentFinderCandidates(filtered, rankMode);
  }, [candidates, rankMode, search, source]);

  if (!open) return null;

  const shortlisted = new Set(shortlistedCatalogIds);
  if (appliedCatalogId) shortlisted.add(appliedCatalogId);
  const importedKeys = new Set(userEquipment.flatMap(entry => {
    const importSource = entry.importSource;
    if (!importSource) return [];
    return [importSource.bomItemId, importSource.oemProductId, importSource.catalogEntryId]
      .filter((key): key is string => key != null);
  }));

  const dutyMW = input.criticalLoadMW ?? input.facilityLoadMW;
  const reservePct = input.reserveMarginPct ?? 15;
  const receptor = input.receptors[0];

  const renderAction = (candidate: EquipmentFinderCandidate): React.JSX.Element => {
    const resolution = candidate.resolution;
    if (resolution.tier === 'catalog-envelope') {
      const inSet = shortlisted.has(resolution.catalogId);
      return (
        <button
          type="button"
          data-testid={`generation-equipment-import-action-${candidate.key}`}
          disabled={inSet}
          onClick={() => onShortlistCatalog(resolution.catalogId)}
          className="inline-flex items-center gap-1 border border-primary bg-primary px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-white hover:opacity-90 disabled:border-border disabled:bg-background disabled:text-text-muted rounded-md"
        >
          {inSet ? <><CheckCircle2 className="h-3 w-3" /> In your set</> : 'Add envelope to set'}
        </button>
      );
    }
    if (resolution.tier === 'prefill-full' || resolution.tier === 'prefill-partial') {
      const importedKey = resolution.prefill.importSource.bomItemId
        ?? resolution.prefill.importSource.oemProductId
        ?? resolution.prefill.importSource.catalogEntryId
        ?? candidate.key;
      return (
        <span className="flex flex-col items-end gap-1.5">
          {importedKeys.has(importedKey) && (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase text-success"><CheckCircle2 className="h-3 w-3" /> In set</span>
          )}
          <button
            type="button"
            data-testid={`generation-equipment-import-action-${candidate.key}`}
            onClick={() => onPrefill(resolution.prefill)}
            className="border border-primary px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-primary hover:bg-primary/5 rounded-md"
          >
            Prefill form
          </button>
        </span>
      );
    }
    return (
      <button
        type="button"
        data-testid={`generation-equipment-import-action-${candidate.key}`}
        onClick={() => {
          if (resolution.tier === 'insufficient' && resolution.identityPrefill) {
            onPrefill({ ...resolution.identityPrefill });
          } else {
            onOpenBlank();
          }
        }}
        className="border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:bg-background rounded-md"
      >
        Start blank form
      </button>
    );
  };

  let rankCounter = 0;
  let lastBand: FinderBand | null = null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      data-testid="generation-equipment-import-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="generation-equipment-library-title"
    >
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col border border-border bg-card rounded-lg">
        <div className="flex items-center gap-2 border-b border-border p-4 pb-3">
          <DatabaseZap className="h-4 w-4 text-primary" aria-hidden />
          <h3 id="generation-equipment-library-title" className="flex-1 text-xs font-bold uppercase tracking-[0.2em] text-text-primary">
            Equipment Library
          </h3>
          <div className="flex overflow-hidden border border-border rounded-md">
            <button
              type="button"
              data-testid="generation-equipment-library-mode-scenario"
              onClick={() => setLibraryMode('scenario-fit')}
              className={`border-r border-border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
                libraryMode === 'scenario-fit' ? 'bg-primary/10 text-primary' : 'text-text-muted'
              }`}
            >
              Scenario fit
            </button>
            <button
              type="button"
              data-testid="generation-equipment-library-mode-full"
              onClick={() => setLibraryMode('full-oem')}
              className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
                libraryMode === 'full-oem' ? 'bg-primary/10 text-primary' : 'text-text-muted'
              }`}
            >
              Full OEM index
            </button>
          </div>
          <button type="button" aria-label="Close equipment library" className="text-text-muted hover:text-text-primary" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {libraryMode === 'full-oem' ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            <EquipmentLibraryBrowser companyId={companyId} pageSize={20} />
          </div>
        ) : (
          <>
        {/* Duty context strip — the scenario the ranking is screened against. */}
        <div className="grid grid-cols-2 gap-px border-b border-border bg-border sm:grid-cols-5" data-testid="generation-equipment-library-duty">
          <div className="bg-primary/5 px-3 py-2"><span className="block text-[10px] uppercase tracking-wider text-text-muted">Screening duty</span><strong className="text-xs">{fmt(dutyMW)} MW · {criterionLabel} + {fmt(reservePct)}%</strong></div>
          <div className="bg-card px-3 py-2"><span className="block text-[10px] uppercase tracking-wider text-text-muted">Firm gas</span><strong className="text-xs">{input.availableGasCapacityDthPerDay != null ? `${fmt(input.availableGasCapacityDthPerDay)} Dth/d` : '—'}</strong>{input.availableGasCapacityDthPerDay == null && <span className="block text-[10px] text-text-muted">undocumented</span>}</div>
          <div className="bg-card px-3 py-2"><span className="block text-[10px] uppercase tracking-wider text-text-muted">Water</span><strong className="text-xs">{input.availableWaterConsumptionGalPerDay != null ? `${fmt(input.availableWaterConsumptionGalPerDay / 1_000_000, 1)} M gal/d` : '—'}</strong>{input.availableWaterConsumptionGalPerDay == null && <span className="block text-[10px] text-text-muted">undocumented</span>}</div>
          <div className="bg-card px-3 py-2"><span className="block text-[10px] uppercase tracking-wider text-text-muted">Receptor</span><strong className="text-xs">{receptor ? `${fmt(receptor.distanceM / METRES_PER_FOOT)} ft · ${fmt(receptor.thresholdDbA ?? 45)} dBA` : '—'}</strong>{!receptor && <span className="block text-[10px] text-text-muted">undocumented</span>}</div>
          <div className="bg-card px-3 py-2"><span className="block text-[10px] uppercase tracking-wider text-text-muted">Parcel</span><strong className="text-xs">{parcelAcres != null ? `${fmt(parcelAcres)} ac` : '—'}</strong></div>
        </div>

        {/* Controls: rank-by, source filter, search, manual entry. */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <div className="flex overflow-hidden border border-border rounded-md">
            {RANK_MODES.map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                data-testid={`generation-equipment-library-rank-${mode}`}
                data-active={rankMode === mode ? 'true' : 'false'}
                onClick={() => setRankMode(mode)}
                className={`border-r border-border px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-wider last:border-r-0 ${rankMode === mode ? 'bg-text-primary text-background' : 'bg-card text-text-secondary hover:bg-background'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex overflow-hidden border border-border rounded-md">
            {([['all', 'All sources'], ['bom', `BOM ${bomCount}`], ['oem', 'OEM'], ['catalog', 'Catalog']] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                data-testid={`generation-equipment-library-source-${id}`}
                data-active={source === id ? 'true' : 'false'}
                onClick={() => setSource(id)}
                className={`border-r border-border px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-wider last:border-r-0 ${source === id ? 'border-primary bg-primary/5 text-primary' : 'bg-card text-text-secondary hover:bg-background'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <input
            aria-label="Search equipment library"
            className="min-w-[160px] flex-1 border border-border bg-background px-2 py-1.5 text-xs text-text-primary rounded-md"
            placeholder="Search manufacturer, model, line…"
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
          <button
            type="button"
            data-testid="generation-equipment-library-manual"
            onClick={onOpenBlank}
            className="border border-primary px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider text-primary hover:bg-primary/5 rounded-md"
          >
            ＋ Manual entry
          </button>
        </div>

        {catalogError != null && (
          <p className="border-b border-warning/50 bg-warning/10 px-4 py-2 text-[10px] text-warning" role="status">
            {catalogError} BOM and OEM candidates are unaffected.
          </p>
        )}
        {catalogError == null && catalogEntries == null && (
          <p className="border-b border-border px-4 py-1.5 text-[9px] uppercase tracking-wider text-text-muted" role="status">Loading company catalog…</p>
        )}

        {/* Ranked candidates */}
        <div className="min-h-0 flex-1 overflow-y-auto p-3" data-testid="generation-equipment-library-results">
          {source === 'bom' && bomCount === 0 && (
            <div className="border border-dashed border-border px-4 py-4 text-[10px] text-text-muted rounded-md">
              No active generation lines in the Equipment BOM yet.
              {onOpenBom && (
                <button type="button" className="ml-2 font-bold uppercase text-primary underline-offset-2 hover:underline" onClick={onOpenBom}>
                  Open Equipment BOM
                </button>
              )}
            </div>
          )}
          {visible.map(candidate => {
            const badge = tierBadge(candidate);
            const showBandHeading = rankMode === 'best-fit' && candidate.band !== lastBand;
            lastBand = candidate.band;
            const ranked = candidate.band === 'ranked' || rankMode !== 'best-fit';
            if (ranked) rankCounter += 1;
            const isBest = rankMode === 'best-fit' && candidate.band === 'ranked' && rankCounter === 1;
            return (
              <React.Fragment key={candidate.key}>
                {showBandHeading && (
                  <p className="mb-1 mt-3 text-[9px] font-bold uppercase tracking-[0.18em] text-text-muted first:mt-0">
                    {BAND_HEADINGS[candidate.band]}
                  </p>
                )}
                <div
                  className={`mb-2 grid grid-cols-[38px_1fr_auto] items-start gap-3 border bg-card p-3 ${isBest ? 'border-2 border-primary shadow-[4px_4px_0_0] shadow-primary/10' : 'border-border'} rounded-lg`}
                  data-testid={`generation-equipment-import-row-${candidate.key}`}
                >
                  <span className={`pt-0.5 text-lg font-extrabold leading-none ${isBest ? 'text-primary' : ranked ? 'text-text-muted' : 'text-border'}`}>
                    {ranked ? String(rankCounter).padStart(2, '0') : '··'}
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="text-xs font-bold text-text-primary">{candidate.title}</h4>
                      <span className={`border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${sourceBadgeClass[candidate.source]} rounded-sm`}>{sourceBadgeLabel[candidate.source]}</span>
                      <span className={`border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${badge.className} rounded-sm`}>{badge.label}</span>
                    </div>
                    {candidate.subtitle && <p className="mt-0.5 text-[9px] text-text-muted">{candidate.subtitle}</p>}
                    <p className="mt-1 max-w-[62ch] text-[10px] text-text-secondary">{candidate.why}</p>
                    {candidate.exact && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        <span className={`border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${candidate.exact.clearsReserve ? 'border-success/40 bg-success/10 text-success' : 'border-danger/40 bg-danger/10 text-danger'} rounded-sm`}>
                          {criterionLabel} + reserve {candidate.exact.clearsReserve ? '✓' : '✕'}
                        </span>
                        {candidate.exact.fits.filter(fit => fit.status !== 'not-applicable').map(fit => (
                          <span
                            key={fit.id}
                            title={fit.detail}
                            className={`border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                              fit.status === 'fits'
                                ? 'border-success/40 bg-success/10 text-success'
                                : fit.status === 'exceeds'
                                  ? 'border-danger/40 bg-danger/10 text-danger'
                                  : 'border-border bg-background text-text-muted'
                            } rounded-sm`}
                          >
                            {fit.label} {fit.status === 'fits' ? 'fits' : fit.status === 'exceeds' ? 'exceeded' : 'undoc.'}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-4">
                      <span className="text-[10px]"><span className="block text-[10px] uppercase tracking-wider text-text-muted">{candidate.exact ? 'Site net / blk' : 'Rated'}</span><strong className="tabular-nums">{candidate.exact ? `${fmt(candidate.exact.siteNetMWPerBlock, 1)} MW` : candidate.stats.ratedMWPerUnit != null ? `${fmt(candidate.stats.ratedMWPerUnit, 2)} MW ${candidate.stats.ratedScopeLabel ?? ''}` : '—'}</strong></span>
                      <span className="text-[10px]"><span className="block text-[10px] uppercase tracking-wider text-text-muted">Heat rate</span><strong className="tabular-nums">{candidate.stats.heatRateLabel ?? '—'}</strong></span>
                      <span className="text-[10px]"><span className="block text-[10px] uppercase tracking-wider text-text-muted">{candidate.stats.leadTimeMonthsLow != null && candidate.stats.footprintLabel == null ? 'Lead time' : 'Footprint'}</span><strong className="tabular-nums">{candidate.stats.footprintLabel ?? (candidate.stats.leadTimeMonthsLow != null ? `${fmt(candidate.stats.leadTimeMonthsLow)}–${fmt(candidate.stats.leadTimeMonthsHigh ?? candidate.stats.leadTimeMonthsLow)} mo` : 'request')}</strong></span>
                    </div>
                  </div>
                  <div className="pt-0.5">{renderAction(candidate)}</div>
                </div>
              </React.Fragment>
            );
          })}
          {visible.length === 0 && (source !== 'bom' || bomCount > 0) && (
            <p className="border border-dashed border-border px-4 py-4 text-[10px] text-text-muted rounded-md">No equipment matches the current filters.</p>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
}
